import React from 'react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Refund Policy
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We stand behind our courses with a comprehensive refund policy designed to ensure your satisfaction.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: January 2025
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="prose max-w-none">
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <h2 className="text-2xl font-bold text-green-800 mb-4">30-Day Money-Back Guarantee</h2>
              <p className="text-green-700 mb-4">
                We're confident in the quality of our courses. If you're not completely satisfied with your purchase, 
                we offer a full refund within 30 days of your purchase date.
              </p>
              <div className="flex items-center text-green-800">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">No questions asked within the first 7 days</span>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">Refund Eligibility</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Eligible Refunds</h3>
            <p className="text-gray-700 mb-4">You are eligible for a refund if:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Your refund request is made within 30 days of purchase</li>
              <li>You have completed less than 50% of the course content</li>
              <li>The course does not meet the description provided</li>
              <li>You experience technical issues that prevent course access</li>
              <li>You are not satisfied with the course quality</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Non-Eligible Refunds</h3>
            <p className="text-gray-700 mb-4">Refunds are not available if:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>More than 30 days have passed since purchase</li>
              <li>You have completed more than 50% of the course content</li>
              <li>You have downloaded or saved course materials</li>
              <li>The refund request is made after course completion</li>
              <li>You have violated our Terms of Service</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">How to Request a Refund</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Step 1: Contact Our Support Team</h3>
            <p className="text-gray-700 mb-6">
              Send an email to <strong>karabacakhilal1.1@gmail.com</strong> with the following information:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Your full name and email address</li>
              <li>Order number or transaction ID</li>
              <li>Course name and purchase date</li>
              <li>Reason for refund request</li>
              <li>Any supporting documentation (if applicable)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Step 2: Review Process</h3>
            <p className="text-gray-700 mb-6">
              Our support team will review your request within 2-3 business days. We may contact you 
              for additional information or to discuss your concerns.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Step 3: Refund Processing</h3>
            <p className="text-gray-700 mb-6">
              Once approved, your refund will be processed within 5-7 business days. The refund will 
              be credited to your original payment method.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Refund Processing Times</h2>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Timeline</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-semibold text-sm">1</span>
                  </div>
                  <span className="text-gray-700">Request submitted: Immediate</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-semibold text-sm">2</span>
                  </div>
                  <span className="text-gray-700">Review process: 2-3 business days</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-semibold text-sm">3</span>
                  </div>
                  <span className="text-gray-700">Refund processing: 5-7 business days</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-green-600 font-semibold text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Funds returned: 7-10 business days total</span>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Payment Method Refunds</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Credit/Debit Cards</h3>
            <p className="text-gray-700 mb-6">
              Refunds to credit or debit cards typically appear within 5-7 business days, 
              depending on your bank's processing time.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">PayPal</h3>
            <p className="text-gray-700 mb-6">
              PayPal refunds are usually processed within 24-48 hours and appear in your 
              PayPal account immediately.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Bank Transfers</h3>
            <p className="text-gray-700 mb-6">
              Bank transfer refunds may take 7-14 business days to appear in your account, 
              depending on your financial institution.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Special Circumstances</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Technical Issues</h3>
            <p className="text-gray-700 mb-6">
              If you experience technical difficulties that prevent you from accessing your course, 
              we will work with you to resolve the issue. If resolution is not possible, a full refund 
              will be provided regardless of the 30-day limit.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Course Discontinuation</h3>
            <p className="text-gray-700 mb-6">
              If we discontinue a course you have purchased, we will offer a full refund or 
              provide access to an equivalent course at no additional cost.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Duplicate Purchases</h3>
            <p className="text-gray-700 mb-6">
              If you accidentally purchase the same course twice, we will refund the duplicate 
              purchase immediately upon verification.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Dispute Resolution</h2>
            
            <p className="text-gray-700 mb-6">
              If you are not satisfied with our refund decision, you may escalate your concern 
              to our management team. We are committed to fair resolution of all disputes.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Contact Information</h2>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Refund Support Team</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> karabacakhilal1.1@gmail.com</p>
                <p><strong>Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST</p>
                <p><strong>Response Time:</strong> Within 24 hours</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-yellow-50 rounded-lg">
              <p className="text-yellow-800 text-sm">
                <strong>Important:</strong> This refund policy is subject to applicable laws and regulations. 
                Some jurisdictions may provide additional consumer rights that are not covered by this policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
